import { generateUuid } from '@/shared/utils';
import { coletarPosicaoNavegador } from '@/shared/utils/geoPosicao';

import type { PortalLocalizacao, PortalTrackingEvent } from '../types/portalTypes';

import { semOrigemDoLink } from './origemDoLink';

interface TrackingCapabilities {
  cookiesEnabled: boolean;
  localStorageAvailable: boolean;
  sessionStorageAvailable: boolean;
  geolocationAvailable: boolean;
  isPrivateMode: boolean;
  trackingBlocked: boolean;
}

interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  languages: string[];
  timezone: string;
  timezoneOffset: number;
  screenWidth: number;
  screenHeight: number;
  colorDepth: number;
  pixelRatio: number;
  touchSupport: boolean;
  hardwareConcurrency: number;
  deviceMemory?: number;
}

export function generateSessaoId(): string {
  if (typeof window === 'undefined') {
    return generateUuid();
  }

  const stored = sessionStorage.getItem('portal_sessao_id');
  if (stored) return stored;

  const newId = generateUuid();
  sessionStorage.setItem('portal_sessao_id', newId);
  return newId;
}

function collectDeviceInfo(): DeviceInfo {
  if (typeof window === 'undefined') {
    return {
      userAgent: '',
      platform: '',
      language: '',
      languages: [],
      timezone: '',
      timezoneOffset: 0,
      screenWidth: 0,
      screenHeight: 0,
      colorDepth: 0,
      pixelRatio: 1,
      touchSupport: false,
      hardwareConcurrency: 1,
    };
  }

  const nav = navigator;

  return {
    userAgent: nav.userAgent,
    platform: nav.platform,
    language: nav.language,
    languages: [...nav.languages],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    screenWidth: screen.width,
    screenHeight: screen.height,
    colorDepth: screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
    touchSupport: 'ontouchstart' in window || nav.maxTouchPoints > 0,
    hardwareConcurrency: nav.hardwareConcurrency || 1,
    deviceMemory: (nav as Navigator & { deviceMemory?: number }).deviceMemory,
  };
}

function detectTrackingBlocks(): TrackingCapabilities {
  if (typeof window === 'undefined') {
    return {
      cookiesEnabled: false,
      localStorageAvailable: false,
      sessionStorageAvailable: false,
      geolocationAvailable: false,
      isPrivateMode: true,
      trackingBlocked: true,
    };
  }

  const cookiesEnabled = navigator.cookieEnabled;

  let localStorageAvailable = false;
  try {
    const testKey = '__portal_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    localStorageAvailable = true;
  } catch {
    localStorageAvailable = false;
  }

  let sessionStorageAvailable = false;
  try {
    const testKey = '__portal_test__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    sessionStorageAvailable = true;
  } catch {
    sessionStorageAvailable = false;
  }

  const geolocationAvailable = 'geolocation' in navigator;

  let isPrivateMode = false;
  try {
    const fs = window.indexedDB.open('test');
    fs.onerror = () => {
      isPrivateMode = true;
    };
  } catch {
    isPrivateMode = true;
  }

  const trackingBlocked = !cookiesEnabled || !localStorageAvailable || !sessionStorageAvailable;

  return {
    cookiesEnabled,
    localStorageAvailable,
    sessionStorageAvailable,
    geolocationAvailable,
    isPrivateMode,
    trackingBlocked,
  };
}

function generateFingerprint(deviceInfo: DeviceInfo): string {
  const components = [
    deviceInfo.userAgent,
    deviceInfo.platform,
    deviceInfo.language,
    deviceInfo.timezone,
    `${deviceInfo.screenWidth}x${deviceInfo.screenHeight}`,
    String(deviceInfo.colorDepth),
    String(deviceInfo.pixelRatio),
    String(deviceInfo.hardwareConcurrency),
  ];

  return components.join('|');
}

export async function buildTrackingPayload(
  evento: string,
  sessaoId: string,
  detalhes?: Record<string, unknown>,
  includeGeolocation = false,
): Promise<PortalTrackingEvent> {
  const deviceInfo = collectDeviceInfo();
  const capabilities = detectTrackingBlocks();

  const payload: PortalTrackingEvent = {
    evento,
    canal: 'WEB_PORTAL',
    sessaoId,
    device: {
      ...deviceInfo,
      fingerprint: generateFingerprint(deviceInfo),
      capabilities,
    },
    detalhes: {
      ...detalhes,
      timestamp: new Date().toISOString(),
      currentUrl: typeof window !== 'undefined' ? semOrigemDoLink(window.location.href) : '',
      referrer: typeof document !== 'undefined' ? semOrigemDoLink(document.referrer) : '',
    },
  };

  if (includeGeolocation) {
    const posicao = await coletarPosicaoNavegador();
    const location: PortalLocalizacao | null = posicao
      ? { ...posicao, provider: 'browser_geolocation' }
      : null;
    if (location) {
      payload.localizacao = location;
    } else {
      payload.detalhes = {
        ...payload.detalhes,
        geolocationFailed: true,
        nonCompliance: 'GEOLOCATION_UNAVAILABLE',
      };
    }
  }

  if (capabilities.trackingBlocked) {
    payload.detalhes = {
      ...payload.detalhes,
      nonCompliance: 'TRACKING_BLOCKED',
      blockedFeatures: {
        cookies: !capabilities.cookiesEnabled,
        localStorage: !capabilities.localStorageAvailable,
        sessionStorage: !capabilities.sessionStorageAvailable,
      },
    };
  }

  return payload;
}
